import React, {useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {purchaseMarkingCodes} from 'api/billing';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {LoaderDoted} from 'components/ui-kit/LoaderDoted';
import {Modal} from 'components/ui-kit/Modal';
import {Checkbox} from 'components/ui-kit/Сheckbox';

import {profile$} from 'models/auth';

import {
    ButtonsContainer,
    CheckboxText,
    ErrorMessage,
    FieldContainer,
    FieldsContainer,
    Form,
    ModalContainer,
    ModalContainerSuccess,
    TitleSuccess,
} from './styled';

interface InvoiceGeneratingModalProps {
    show: boolean;
    onClose: () => void;
}

type ModalState = 'form' | 'loading' | 'success';

interface FormData {
    quantity: string;
    agreed: boolean;
}

export const InvoiceGeneratingModal: React.FC<InvoiceGeneratingModalProps> = ({show, onClose}) => {
    const {t} = useTranslation();
    const [state, setState] = useState<ModalState>('form');
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [filename, setFilename] = useState<string | null>(null);
    const profile = useStore(profile$);

    const setTitle = useMemo(() => {
        switch (state) {
            case 'form':
                return t('pages.profile.billing.invoiceGenerating.titleForm');
            case 'loading':
                return t('pages.profile.billing.invoiceGenerating.titleLoading');
            case 'success':
                return t('pages.profile.billing.invoiceGenerating.titleSuccess');
        }
    }, [t, state]);
    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                quantity: yup
                    .string()
                    .required(t('components.form.required'))
                    .test('is-positive', t('components.form.positive'), (value) => {
                        if (!value) return false;
                        const numValue = Number(value);
                        return !isNaN(numValue) && numValue > 0;
                    }),
                agreed: yup
                    .boolean()
                    .required(t('components.form.required'))
                    .oneOf(
                        [true],
                        t('pages.profile.billing.invoiceGenerating.messages.shouldBeAgreed'),
                    ),
            }),
        [t],
    );

    const {
        control,
        handleSubmit,
        formState: {errors, isValid},
        reset,
    } = useForm<FormData>({
        mode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            quantity: '',
            agreed: false,
        },
    });

    const onSubmit = async (data: FormData) => {
        setState('loading');

        try {
            const {blob, filename} = await purchaseMarkingCodes({
                quantity: Number(data.quantity),
                email: profile?.email ?? '',
            });

            setPdfBlob(blob);
            setFilename(filename);
            setState('success');
        } catch (error) {
            setState('form');
            toast.error(t('pages.profile.billing.deposit.messages.error'));
        }
    };

    const handleDownload = () => {
        if (!pdfBlob) return;

        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename ?? `invoice-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setTimeout(() => {
            handleClose();
        }, 1000);
    };

    const handleClose = () => {
        if (state === 'loading') return;

        setState('form');
        reset();
        setPdfBlob(null);
        setFilename(null);
        onClose();
    };

    const renderForm = () => (
        <ModalContainer>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <FieldsContainer>
                    <FieldContainer>
                        <Controller
                            control={control}
                            name="quantity"
                            render={(props) => (
                                <Input
                                    {...props}
                                    placeholder={t(
                                        'pages.profile.billing.invoiceGenerating.quantityPlaceholder',
                                    )}
                                    type="number"
                                    hasError={!!errors.quantity}
                                    label={t(
                                        'pages.profile.billing.invoiceGenerating.quantityLabel',
                                    )}
                                />
                            )}
                        />
                        {errors.quantity && <ErrorMessage>{errors.quantity.message}</ErrorMessage>}
                    </FieldContainer>
                    <FieldContainer>
                        <Controller
                            control={control}
                            name="agreed"
                            render={(props) => (
                                <Checkbox
                                    checked={props.value}
                                    onChange={(e) => props.onChange(e.target.checked)}
                                >
                                    <CheckboxText>
                                        {t(
                                            'pages.profile.billing.invoiceGenerating.agreementLabel',
                                        )}
                                    </CheckboxText>
                                </Checkbox>
                            )}
                        />
                        {errors.agreed && <ErrorMessage>{errors.agreed.message}</ErrorMessage>}
                    </FieldContainer>
                </FieldsContainer>
                <ButtonsContainer>
                    <Button variant={ButtonVariant.Outline} type="button" onClick={handleClose}>
                        {t('pages.profile.billing.invoiceGenerating.buttons.cancel')}
                    </Button>
                    <Button variant={ButtonVariant.Normal} type="submit" disabled={!isValid}>
                        {t('pages.profile.billing.invoiceGenerating.buttons.buy')}
                    </Button>
                </ButtonsContainer>
            </Form>
        </ModalContainer>
    );

    const renderSuccess = () => (
        <ModalContainerSuccess>
            <TitleSuccess>{t('pages.profile.billing.invoiceGenerating.successTitle')}</TitleSuccess>
            <Button variant={ButtonVariant.Normal} type="button" onClick={handleDownload}>
                {t('pages.profile.billing.invoiceGenerating.buttons.download')}
            </Button>
        </ModalContainerSuccess>
    );

    return (
        <Modal
            title={setTitle}
            show={show}
            modalWidth={0}
            onClose={handleClose}
            hideCloseButton={state !== 'form'}
            closable={state === 'form'}
        >
            {state === 'form' && renderForm()}
            {state === 'loading' && <LoaderDoted />}
            {state === 'success' && renderSuccess()}
        </Modal>
    );
};
